import React, { useState } from 'react';
import { _T, C } from '../../shared/theme';
import { SOUND_THEME_DEFS, SOUND_THEME_IDS, previewThemeSound, playSound } from '../../shared/soundEngine';
import { syncKey } from '../../shared/dbService';

const OFF = {
  id: 'off', icon: '🔇',
  name: 'Off',      nameNL: 'Uit',
  desc: 'No sound effects', descNL: 'Geen geluidseffecten',
  color: '#8899BB',
};

export default function SoundThemePicker({ lang, license, maxGameLevel = 0 }) {
  const nl = lang === 'nl';
  const isFreemium = !license || license === 'freemium';
  const [selected, setSelected] = useState(() => localStorage.getItem('sl_sound_theme') || 'off');

  const pick = (id) => {
    if (isFreemium && id !== 'off') return;
    const def = SOUND_THEME_DEFS[id];
    if (def?.gameLvl && maxGameLevel < def.gameLvl) return;
    setSelected(id);
    localStorage.setItem('sl_sound_theme', id);
    syncKey('sl_sound_theme', id);
    if (id !== 'off') playSound('click');
  };

  const options = [OFF, ...SOUND_THEME_IDS.map(id => ({ id, ...SOUND_THEME_DEFS[id] }))];

  return (
    <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: _T.accent, letterSpacing: 3, fontWeight: 600 }}>
          🔊 {nl ? 'GELUIDSTHEMA' : 'SOUND THEME'}
        </div>
        {isFreemium && (
          <span style={{ fontSize: 10, color: '#C8A84B', background: 'rgba(200,168,75,0.12)', border: '1px solid rgba(200,168,75,0.3)', borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>
            ⭐ PREMIUM
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {options.map(opt => {
          const active     = selected === opt.id;
          const gameLocked = opt.gameLvl && maxGameLevel < opt.gameLvl;
          const locked     = (isFreemium && opt.id !== 'off') || gameLocked;

          return (
            <div
              key={opt.id}
              onClick={() => pick(opt.id)}
              style={{
                position: 'relative',
                background: active ? `${opt.color}18` : _T.bg4,
                border: `2px solid ${active ? opt.color : _T.cardBorderBot}`,
                borderRadius: 14,
                padding: '12px 14px',
                cursor: locked ? 'default' : 'pointer',
                opacity: locked ? 0.5 : 1,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {/* Lock badge */}
              {gameLocked && (
                <div style={{ position: 'absolute', top: 7, right: 9, fontSize: 9, color: '#C8A84B', fontWeight: 700, textAlign: 'right', lineHeight: 1.3 }}>🎮<br/>Lvl {opt.gameLvl}</div>
              )}
              {!gameLocked && isFreemium && opt.id !== 'off' && (
                <div style={{ position: 'absolute', top: 7, right: 9, fontSize: 11, color: '#C8A84B' }}>🔒</div>
              )}

              {/* Active checkmark */}
              {active && (
                <div style={{ position: 'absolute', top: 7, right: 9, width: 16, height: 16, borderRadius: '50%', background: opt.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 9, color: '#0a1220', fontWeight: 900 }}>✓</span>
                </div>
              )}

              <div style={{ fontSize: 22, marginBottom: 4, lineHeight: 1 }}>{opt.icon}</div>
              <div style={{ color: active ? opt.color : C.white, fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                {nl ? opt.nameNL : opt.name}
              </div>
              <div style={{ color: C.silver, fontSize: 10, lineHeight: 1.3, marginBottom: opt.id !== 'off' && !locked ? 8 : 0 }}>
                {nl ? opt.descNL : opt.desc}
              </div>

              {/* Preview button — paid users, non-off themes */}
              {!locked && opt.id !== 'off' && (
                <button
                  onClick={e => { e.stopPropagation(); previewThemeSound(opt.id, 'gift'); }}
                  style={{
                    background: `${opt.color}20`,
                    border: `1px solid ${opt.color}55`,
                    borderRadius: 8,
                    padding: '3px 10px',
                    color: opt.color,
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-block',
                  }}
                >
                  ▶ {nl ? 'Voorbeeld' : 'Preview'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
